import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./HomePage.module.scss";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import Card from "../../components/Card/Card";
import TreeLayout from "../../components/TreeLayout/TreeLayout";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { useMediaQuery } from "@mui/material";
import Separator from "../../components/Separator/Separator";
import KanbanBoard from "../../layouts/KanbanBoard/KanbanBoard";
import { updateFloatingBarVisible } from "../../redux/layoutStore";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  BookOpen,
  FileSearch,
  FileText,
  LayoutDashboard,
  Shield,
  Target,
  TrendingUp,
  Calendar,
} from "lucide-react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const IMPACT_SCORES = { low: 1, medium: 2, high: 3 };
const CLOSED_STATUSES = ["done", "closed"];
const STATUS_ORDER = ["Open", "In Progress", "In Review", "Done"];
const STATUS_COLORS = {
  Open: "#C5C5C5",
  "In Progress": "#FF7427",
  "In Review": "#AD88C6",
  Done: "#16A249",
};
const SEVERITY_LABELS = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "None",
  unspecified: "Unspecified",
};

const isClosedStatus = (status) => CLOSED_STATUSES.includes(status?.toLowerCase?.());
const getImpactScore = (level) => IMPACT_SCORES[level?.toLowerCase?.()] ?? 0;
const getSafeDateValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};
const countConfluencePages = (node) => {
  if (!node) return 0;
  const children = Array.isArray(node.children) ? node.children : [];
  const childCount = children.reduce((total, child) => total + countConfluencePages(child), 0);
  return (node.page_id ? 1 : 0) + childCount;
};
const getRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const days = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const MetricCard = ({
  title,
  value,
  icon: Icon,
  iconClassName,
  valueClassName,
  color,
  subtitle,
  to,
}) => {
  const formattedValue =
    typeof value === "number" ? value.toLocaleString("en-US") : value;

  const metricValueClass = valueClassName
    ? `${styles.metricValue} ${styles[valueClassName]}`
    : styles.metricValue;

  const content = (
    <div className={styles.metricCard}>
      <div className={styles.metricHeader}>
        <span className={styles.metricTitle}>{title}</span>
        {Icon && (
          <Icon
            size={18}
            className={`${styles.metricIcon} ${iconClassName ? styles[iconClassName] : ""}`}
            style={color ? { color } : undefined}
          />
        )}
      </div>
      <div className={metricValueClass} style={color ? { color } : undefined}>
        {formattedValue}
      </div>
      {subtitle && <div className={styles.metricSubtitle}>{subtitle}</div>}
    </div>
  );

  return to ? (
    <Link to={to} className={styles.metricLink}>
      {content}
    </Link>
  ) : (
    content
  );
};

const DashboardCharts = ({ auditData, findingData }) => {
  const auditChartRef = useRef(null);
  const auditCanvasRef = useRef(null);
  const findingChartRef = useRef(null);
  const findingCanvasRef = useRef(null);

  useEffect(() => {
    if (auditChartRef.current) {
      auditChartRef.current.destroy();
      auditChartRef.current = null;
    }

    if (!auditData || auditData.length === 0 || !auditCanvasRef.current) return;

    auditChartRef.current = new Chart(auditCanvasRef.current, {
      type: "doughnut",
      data: {
        labels: auditData.map((item) => item.label),
        datasets: [
          {
            data: auditData.map((item) => item.value),
            backgroundColor: auditData.map((item) => item.color),
            borderWidth: 1,
          },
        ],
      },
      options: {
        cutout: "55%",
        plugins: {
          legend: {
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.label}: ${context.raw}`,
            },
          },
        },
      },
    });

    return () => {
      auditChartRef.current?.destroy();
      auditChartRef.current = null;
    };
  }, [auditData]);

  useEffect(() => {
    if (findingChartRef.current) {
      findingChartRef.current.destroy();
      findingChartRef.current = null;
    }

    if (!findingData || findingData.length === 0 || !findingCanvasRef.current) return;

    findingChartRef.current = new Chart(findingCanvasRef.current, {
      type: "bar",
      data: {
        labels: findingData.map((item) => item.label),
        datasets: [
          {
            label: "Open",
            data: findingData.map((item) => item.open),
            backgroundColor: "#FF6B6B",
          },
          {
            label: "Closed",
            data: findingData.map((item) => item.closed),
            backgroundColor: "#007940",
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${context.raw}`,
            },
          },
        },
        scales: {
          x: {
            stacked: false,
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          },
        },
      },
    });

    return () => {
      findingChartRef.current?.destroy();
      findingChartRef.current = null;
    };
  }, [findingData]);

  return (
    <div className={styles.chartsGrid}>
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>Audit Status Distribution</div>
        <div className={styles.chartBody}>
          {auditData?.length ? (
            <canvas
              ref={auditCanvasRef}
              height="200"
              style={{ height: "200px", maxHeight: "240px", width: "100%" }}
            />
          ) : (
            <div className={styles.emptyState}>No audit data available</div>
          )}
        </div>
      </div>
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>Findings by Severity</div>
        <div className={styles.chartBody}>
          {findingData?.length ? (
            <canvas
              ref={findingCanvasRef}
              height="200"
              style={{ height: "200px", maxHeight: "240px", width: "100%" }}
            />
          ) : (
            <div className={styles.emptyState}>No finding data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const { t } = useTranslation();

  useFetchOrganizationData();

  const modules = useSelector((state) => state.user.modules);
  const {
    auditsTasksData,
    findingsData,
    improvementsData,
    risksData,
    permits,
  } = useSelector((state) => state.organization);
  const confluenceTreeData = useSelector(
    (state) => state.confluence.confluenceTreeData,
  );

  const [organizationModules, setOrganizationModules] = useState(null);

  useEffect(() => {
    dispatch(updateFloatingBarVisible(true)); //
  }, [dispatch]);

  useEffect(() => {
    // dispatch(setLoadingContext({ context: PAGE_LOADER, isLoading: true }));

    if (modules && modules[orgId]) {
      setOrganizationModules(modules[orgId]);
      // dispatch(setLoadingContext({ context: PAGE_LOADER, isLoading: false }));
    }
  }, [modules, orgId]);

  const dashboardData = useMemo(() => {
    const audits = Object.values(auditsTasksData || {}).filter(
      (audit) => !audit.archived,
    );
    const activeAudits = audits.filter((audit) => !isClosedStatus(audit.status));

    const auditStatusCounts = audits.reduce((acc, audit) => {
      const status = audit.status || "Open";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    const auditChartData = STATUS_ORDER.map((status) => ({
      label: status,
      value: auditStatusCounts[status] || 0,
      color: STATUS_COLORS[status],
    })).filter((item) => item.value > 0);

    const findings = Object.values(findingsData || {}).filter(
      (finding) => !finding.archived,
    );
    const openFindings = findings.filter(
      (finding) => !isClosedStatus(finding.status),
    );
    const severityBuckets = findings.reduce((acc, finding) => {
      const levelKey = finding?.risk?.level?.toLowerCase?.() || "unspecified";
      const key = SEVERITY_LABELS[levelKey] ? levelKey : "unspecified";

      if (!acc[key]) {
        acc[key] = {
          key,
          label: SEVERITY_LABELS[key] || "Unspecified",
          open: 0,
          closed: 0,
        };
      }

      if (isClosedStatus(finding.status)) {
        acc[key].closed += 1;
      } else {
        acc[key].open += 1;
      }

      return acc;
    }, {});
    const findingChartData = Object.values(severityBuckets).filter(
      (bucket) => bucket.open || bucket.closed,
    );

    const improvements = Object.values(improvementsData || {}).filter(
      (item) => !item.archived,
    );
    const activeImprovements = improvements.filter(
      (item) => !isClosedStatus(item.status),
    );
    const completedImprovements = improvements.filter((item) =>
      isClosedStatus(item.status),
    );
    // Current mitigation proxy: completed improvements / total improvements.
    // TODO: replace with backend risk metrics (mitigated vs active risks) when available.
    const mitigationProgress = improvements.length
      ? Math.round((completedImprovements.length / improvements.length) * 100)
      : 0;

    const risks = Object.values(risksData || {}).filter((risk) => !risk.archived);
    const activeRisks = risks.filter(
      (risk) => (risk.level || "").toLowerCase() !== "none",
    );
    const criticalRisks = risks.filter(
      (risk) => (risk.level || "").toLowerCase() === "high",
    );
    const riskScore = activeRisks.reduce(
      (total, risk) => total + getImpactScore(risk.level),
      0,
    );

    const qmsAmendments = risks
      .map((risk) => ({
        id: risk.risk_id || risk.qms_id || risk.id,
        title: risk.qms_description || risk.description,
        level: risk.level,
        qmsId: risk.qms_id,
        updatedAt:
          risk.updated_at ||
          risk.updatedAt ||
          risk.updated ||
          risk.created_at ||
          risk.createdAt,
      }))
      .filter((item) => item.title)
      .sort((a, b) => getSafeDateValue(b.updatedAt) - getSafeDateValue(a.updatedAt))
      .slice(0, 4);

    const statCards = [
      {
        key: "audits",
        title: "Active Audits",
        value: activeAudits.length,
        icon: FileSearch,
        iconClassName: "iconAudits",
        valueClassName: "valueAudits",
        color: "#133027",
        to: orgId ? `/${orgId}/audits` : undefined,
      },
      {
        key: "findings",
        title: "Open Findings",
        value: openFindings.length,
        icon: AlertCircle,
        iconClassName: "iconFindings",
        valueClassName: "valueFindings",
        color: "#EF4343",
        to: orgId ? `/${orgId}/findings` : undefined,
      },
      {
        key: "improvements",
        title: "Improvement Projects",
        value: activeImprovements.length,
        icon: TrendingUp,
        iconClassName: "iconImprovements",
        valueClassName: "valueImprovements",
        color: "#16A249",
        to: orgId ? `/${orgId}/improvement-projects` : undefined,
      },
      {
        key: "qms",
        title: "QMS Initiatives",
        value: Object.values(permits || {}).length,
        icon: Target,
        iconClassName: "iconQms",
        valueClassName: "valueQms",
        color: "#E0EEFA",
        to: orgId ? `/${orgId}/qms` : undefined,
      },
      {
        key: "knowledge",
        title: "Knowledge Base Docs",
        value: countConfluencePages(confluenceTreeData),
        icon: BookOpen,
        iconClassName: "iconKnowledge",
        valueClassName: "valueKnowledge",
        color: "#D8D4FC",
        to: orgId ? `/${orgId}/knowledge-base` : undefined,
      },
      {
        key: "risks",
        title: "Active Risks",
        value: activeRisks.length,
        icon: Shield,
        iconClassName: "iconRisks",
        valueClassName: "valueRisks",
        color: "#EF4343",
        subtitle: `${criticalRisks.length} critical`,
        to: orgId ? `/${orgId}/risk-profile` : undefined,
      },
    ];

    return {
      statCards,
      auditChartData,
      findingChartData,
      riskMetrics: {
        active: activeRisks.length,
        critical: criticalRisks.length,
        mitigated: completedImprovements.length,
        totalScore: riskScore,
        progress: mitigationProgress,
      },
      qmsAmendments,
    };
  }, [
    auditsTasksData,
    findingsData,
    improvementsData,
    risksData,
    permits,
    confluenceTreeData,
    orgId,
  ]);

  const filterModulesForNonMobile = (modules) => {
    // Exclude "New Finding" module for non-mobile
    return modules?.filter((module) => module.title !== "New Finding");
  };

  const homeModule = organizationModules?.find(
    (module) => module.title === "Home",
  );

  const newFindingModule = organizationModules?.find(
    (module) => module.title === "New Finding",
  );

  const filterModulesByCategory = (category) => {
    const filteredModules = isMobile
      ? organizationModules
      : filterModulesForNonMobile(organizationModules);

    return filteredModules
      ? filteredModules.filter(
        (module) => module.category?.toLowerCase() === category.toLowerCase(),
      )
      : [];
  };

  return (
    <MainLayout>
      <div className={styles.homePageWrapper}>
        {isMobile ? (
          // Show only "New Finding" for mobile users
          newFindingModule ? (
            <div className={styles.mobileCardWrapper}>
              <Card
                category="newFinding"
                label={t("modules.newfinding")}
                modules={[newFindingModule]} // Render only "New Finding" card
              />
            </div>
          ) : (
            <p>No modules available</p>
          )
        ) : (
          // Show the full view for non-mobile users
          homeModule && (
            <>
              <div className={styles.dashboardHeader}>
                <h1 className={styles.dashboardTitle}>
                  <LayoutDashboard
                    size={26}
                    className={styles.dashboardTitleIcon}
                    aria-hidden
                  />
                  <span>Dashboard</span>
                </h1>
                <p className={styles.dashboardSubtitle}>
                  Your comprehensive compliance management platform
                </p>
              </div>

              <div className={styles.statGrid}>
                {dashboardData.statCards.map((card) => (
                  <MetricCard
                    key={card.key}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    iconClassName={card.iconClassName}
                    valueClassName={card.valueClassName}
                    color={card.color}
                    subtitle={card.subtitle}
                    to={card.to}
                  />
                ))}
              </div>

              <DashboardCharts
                auditData={dashboardData.auditChartData}
                findingData={dashboardData.findingChartData}
              />

              <div className={styles.qmsCard}>
                <div className={styles.qmsHeader}>
                  <FileText size={20} className={styles.qmsIcon} />
                  <div className={styles.cardTitle}>Latest QMS Amendments</div>
                </div>
                {dashboardData.qmsAmendments.length ? (
                  <div className={styles.qmsListScroll}>
                    <div className={styles.qmsList}>
                      {dashboardData.qmsAmendments.map((item) => (
                        <div key={item.id} className={styles.qmsItem}>
                          <div className={styles.qmsItemTop}>
                            <div>
                              <div className={styles.qmsItemTitle}>{item.title}</div>
                              <div className={styles.qmsMeta}>
                                {item.updatedAt && (
                                  <span className={styles.metaChip}>
                                    <Calendar size={12} />
                                    {getRelativeTime(item.updatedAt)}
                                  </span>
                                )}
                                {item.qmsId && (
                                  <span className={`${styles.metaPill} ${styles.metaOutline}`}>
                                    {item.qmsId}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={styles.qmsBottomRow}>
                            {/* TODO: CHANGE CONFLUENCE SYNC LATER WITH REAL DATA */}
                            <span className={styles.syncPill}>Confluence Sync</span>
                            {item.level && (
                              <span
                                className={`${styles.badge} ${styles[`level${item.level?.toLowerCase?.()}`] ||
                                  styles.levelNeutral
                                  }`}
                              >
                                {SEVERITY_LABELS[item.level?.toLowerCase?.()] || item.level}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyState}>No QMS updates yet.</div>
                )}
              </div>

              <div className={styles.riskCard}>
                <div className={styles.riskHeader}>
                  <Shield className={styles.riskIcon} size={20} />
                  <div className={styles.riskHeadline}>Risk Control Center</div>
                </div>
                <div className={styles.riskMetricsRow}>
                  <div className={styles.riskMetricBlock}>
                    <div className={styles.riskMetricLabel}>Active Risks</div>
                    <div className={styles.riskMetricValue}>
                      {dashboardData.riskMetrics.active}
                    </div>
                  </div>
                  <div className={styles.riskMetricBlock}>
                    <div className={styles.riskMetricLabel}>Critical</div>
                    <div className={`${styles.riskMetricValue} ${styles.riskCritical}`}>
                      {dashboardData.riskMetrics.critical}
                    </div>
                  </div>
                </div>
                <div className={styles.progressRow}>
                  <div className={styles.progressHeader}>
                    <span>Mitigation Progress</span>
                    <span className={styles.progressPercent}>
                      {dashboardData.riskMetrics.progress}%
                    </span>
                  </div>
                  <div className={styles.riskProgressBar}>
                    <div
                      className={styles.riskProgressFill}
                      style={{
                        width: `${Math.min(
                          Math.max(dashboardData.riskMetrics.progress, 0),
                          100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.riskFooter}>
                  <span>
                    Total Risk Score:{" "}
                    <strong>{dashboardData.riskMetrics.totalScore}</strong>
                  </span>
                  <Link
                    to={orgId ? `/${orgId}/risk-profile` : "#"}
                    className={styles.inlineLinkAccent}
                  >
                    Click to view unified Risk Control Center →
                  </Link>
                </div>
              </div>

              <div className={styles.welcomeCard}>
                <div className={styles.welcomeTitle}>Welcome to Borderly+</div>
                <p className={styles.welcomeText}>
                  Your comprehensive compliance management platform for handling audits,
                  findings, improvement projects, quality management systems, and risk
                  intelligence.
                </p>
                <p className={styles.welcomeText}>
                  <span className={styles.accentArrow}>→</span> Use the sidebar to navigate
                  between modules and start managing your compliance data.
                </p>
              </div>

              {/* Legacy module cards are kept for reference but hidden on the dashboard */}
              {/* <div className={styles.cardWrapper}>
                <Card
                  category="define"
                  label={t("treeLayout.columns.define")}
                  modules={filterModulesByCategory("Define")}
                />
                <Card
                  category="measure"
                  label={t("treeLayout.columns.measure")}
                  modules={filterModulesByCategory("Measure")}
                />
                <Card
                  category="analyse"
                  label={t("treeLayout.columns.analyse")}
                  modules={filterModulesByCategory("Analyse")}
                />
                <Card
                  category="improve"
                  label={t("treeLayout.columns.improve")}
                  modules={filterModulesByCategory("Improve")}
                />
                <Card
                  category="control"
                  label={t("treeLayout.columns.control")}
                  modules={filterModulesByCategory("Control")}
                />
              </div> */}
              <Separator className={styles.separator} />
              <KanbanBoard />
              <Separator className={styles.separator} />
              {!isMobile && <TreeLayout />}
            </>
          )
        )}
      </div>
    </MainLayout>
  );
};

export default HomePage;
